/**
 * Email Template Context
 *
 * Provides shared email template state across the app.
 * Seeded from DEFAULT_EMAIL_TEMPLATES (the same defaults used in Settings > Templates).
 * This allows the billing configuration sections in project creation and template
 * forms to read from the same source as the Settings page.
 *
 * TODO (Engineering): Replace DEFAULT_EMAIL_TEMPLATES seed + localStorage with
 * GET /api/email-templates on mount and POST/PUT/DELETE mutations.
 */

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { DEFAULT_EMAIL_TEMPLATES } from '@/components/billing/EmailTemplateEditor';
import type { EmailTemplate } from '@/components/billing/EmailTemplateEditor';

interface EmailTemplateContextType {
  emailTemplates: EmailTemplate[];
  setEmailTemplates: React.Dispatch<React.SetStateAction<EmailTemplate[]>>;
  getEmailTemplatesByType: (type: EmailTemplate['type']) => EmailTemplate[];
}

const EmailTemplateContext = createContext<EmailTemplateContextType | undefined>(undefined);

export function EmailTemplateProvider({ children }: { children: ReactNode }) {
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(DEFAULT_EMAIL_TEMPLATES);

  const getEmailTemplatesByType = useCallback(
    (type: EmailTemplate['type']) => emailTemplates.filter((t) => t.type === type),
    [emailTemplates],
  );

  return (
    <EmailTemplateContext.Provider value={{ emailTemplates, setEmailTemplates, getEmailTemplatesByType }}>
      {children}
    </EmailTemplateContext.Provider>
  );
}

export function useEmailTemplates() {
  const context = useContext(EmailTemplateContext);
  if (context === undefined) {
    throw new Error('useEmailTemplates must be used within an EmailTemplateProvider');
  }
  return context;
}
